import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { login } from "api/auth";

// --- Компоненты иконок ---

const MailIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

const LockIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
    />
  </svg>
);

const EyeIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const EyeSlashIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L6.228 6.228"
    />
  </svg>
);

// --- Основной компонент формы ---

export const LoginForm = () => {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const signInPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (!email || !password) {
      setError('Email va parol kiritilishi shart');
      setLoading(false);
      return;
    }

    try {
      const data = await login(email, password);

      // Update auth context
      const session = {
        user: {
          id: data.user.id,
          email: data.user.email,
          username: data.user.username,
          app_user: data.app_user, // Store app_user info
        },
        access_token: data.access_token,
      };

      setSession(session);

      // Navigate based on user type
      if (data.app_user) {
        nav("/employee");
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      setError(err.message || 'Xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const signInMagic = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);

    if (!email) {
      setError('Email kiritilishi shart');
      setLoading(false);
      return;
    }

    setMessage("Magic link funksiyasi keyinroq qo'shiladi. Iltimos, oddiy kirishdan foydalaning.");
    setLoading(false);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-cyan-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 transition-colors">
      {/* Контейнер для анимированных "блобов" на заднем фоне */}
      <div className="absolute inset-0 h-full w-full">
        <div className="absolute top-0 -left-1/4 h-72 w-72 animate-blob rounded-full bg-purple-300 mix-blend-multiply opacity-70 filter blur-xl"></div>
        <div className="absolute top-0 -right-1/4 h-72 w-72 animate-blob rounded-full bg-yellow-300 mix-blend-multiply opacity-70 filter blur-xl animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 h-72 w-72 animate-blob rounded-full bg-pink-300 mix-blend-multiply opacity-70 filter blur-xl animation-delay-4000"></div>
      </div>

      {/* Карточка формы с эффектом стекла */}
      <div className="relative z-10 w-full max-w-md space-y-6 rounded-2xl border border-white/50 dark:border-slate-700/50 bg-white/70 dark:bg-slate-800/70 p-8 shadow-2xl backdrop-blur-xl">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Xush kelibsiz!</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Kirish ma'lumotlaringizni kiriting</p>
        </div>

        {/* Уведомления об ошибках и сообщениях */}
        {error && (
          <p className="animate-pulse rounded-lg bg-red-100 p-3 text-center text-sm text-red-700">
            {error}
          </p>
        )}
        {message && (
          <p className="rounded-lg bg-green-100 p-3 text-center text-sm text-green-700">
            {message}
          </p>
        )}

        <form onSubmit={signInPassword} className="space-y-5">
          {/* Поле Email */}
          <div className="relative">
            <MailIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              placeholder="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border-2 border-gray-200 dark:border-slate-600 bg-white/80 dark:bg-slate-700/80 py-2.5 pl-10 pr-3 outline-none transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Поле Пароль */}
          <div className="relative">
            <LockIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Parol"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border-2 border-gray-200 dark:border-slate-600 bg-white/80 dark:bg-slate-700/80 py-2.5 pl-10 pr-10 outline-none transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 text-gray-900 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-600"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Кнопка входа */}
          <button
            type="submit"
            disabled={loading}
            className="w-full transform rounded-lg bg-gradient-to-r from-indigo-600 to-blue-500 py-3 font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl focus:ring-4 focus:ring-indigo-500/50 disabled:transform-none disabled:opacity-70"
          >
            {loading ? "Kirilmoqda…" : "Kirish"}
          </button>
        </form>

        {/* Ссылка для альтернативного входа */}
        <div className="text-center">
          <button
            onClick={signInMagic}
            disabled={!email || loading}
            className="font-medium text-indigo-600 transition hover:text-indigo-500 disabled:text-gray-400"
          >
            Sehrli havola orqali kirish
          </button>
        </div>
      </div>
    </div>
  );
};
